import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import { cn } from "../lib/utils";
import { stageColor } from "../lib/taskStage";
import { History, User } from "lucide-react";

// When the task moved to which stage of its timeline, and who moved it.
const TaskStageHistory = ({ history = [], stages = [], maxItems = 20 }) => {
  const stageById = Object.fromEntries((stages || []).map((st) => [st.id, st]));
  const entries = (history || [])
    .map((entry) => {
      const stage = stageById[entry.stage_id];
      const name = stage?.name || entry.stage_name;
      return name ? { ...entry, name, color: stage?.color } : null;
    })
    .filter(Boolean)
    .reverse();
  if (entries.length === 0) return null;

  const formatDateTime = (iso) => (iso
    ? new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })
    : "—");

  return (
    <Card className="bg-zinc-900/50 border-zinc-800">
      <CardHeader className="pb-1 pt-2 px-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-zinc-50 flex items-center gap-2 text-sm">
            <History className="h-4 w-4 text-purple-400" />
            Stage History
          </CardTitle>
          <Badge variant="outline" className="bg-zinc-800 text-zinc-400 border-zinc-700 text-xs">
            {entries.length} moves
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="pt-1 pb-2 px-3">
        <div className="space-y-1.5 max-h-48 overflow-y-auto pr-1">
          {entries.slice(0, maxItems).map((entry, idx) => (
            <div key={idx} className="flex items-start gap-2 text-xs border-l-2 border-zinc-700 pl-2 py-1 hover:bg-zinc-800/30 rounded-r">
              <span className="mt-1 h-2 w-2 rounded-full shrink-0" style={{ backgroundColor: stageColor(entry.color) }} />
              <div className="flex-1 min-w-0">
                <span className="font-medium text-zinc-200">{entry.name}</span>
                {entry.notes && <span className="text-zinc-500"> · {entry.notes}</span>}
                <div className={cn("flex items-center gap-2 text-zinc-500 text-[10px] mt-0.5")}>
                  {entry.updated_by_name && (
                    <>
                      <User className="h-2.5 w-2.5" />
                      <span>{entry.updated_by_name}</span>
                      <span>•</span>
                    </>
                  )}
                  <span>{formatDateTime(entry.entered_at)}</span>
                </div>
              </div>
            </div>
          ))}
          {entries.length > maxItems && (
            <p className="text-[10px] text-zinc-500 text-center py-1">+ {entries.length - maxItems} more</p>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default TaskStageHistory;
